import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import TestQuestion from '../components/test/TestQuestion';
import TestProgress from '../components/test/TestProgress';
import TestControls from '../components/test/TestControls';
import FreeTrialLimitModal from '../components/auth/FreeTrialLimitModal';
import ConfirmAbandonModal from '../components/test/ConfirmAbandonModal';
import ExplanationBox from '../components/test/ExplanationBox';
import ReviewHeader from '../components/test/ReviewHeader';
import ReviewCompletion from '../components/test/ReviewCompletion';
import EmptyReview from '../components/test/EmptyReview';
import useReviewLogic from '../hooks/useReviewLogic';
import styles from '../styles/test/TestStyles.module.css';

/**
 * Repaso component - Review mode for failed questions
 */
const Repaso = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'viajeros';
  const { isDarkMode } = useTheme();
  const letras = ['A', 'B', 'C', 'D'];
  
  // Use the review logic hook
  const {
    preguntasFalladas,
    preguntaActual,
    respuestas,
    modoRepaso,
    showTrialLimitModal,
    showConfirmModal,
    preguntaActualObj,
    respuestaActual,
    hasNextQuestion,
    isLastQuestion,
    isEmpty,
    setShowTrialLimitModal,
    setShowConfirmModal,
    handleRespuesta,
    handlePreviousQuestion,
    handleNextQuestion,
    handleSaltar,
    handleLimpiarFallos,
    handleCompletion,
    handleConfirmAbandon,
    handleRestartReview
  } = useReviewLogic({ type });

  // If there are no questions to review
  if (isEmpty) {
    return (
      <div className={styles.testPageContainer}>
        <div className={styles.testContent}>
          <div className={styles.container}>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'center' }}>
              Repaso de Preguntas Falladas {type === 'viajeros' ? 'Viajeros' : 'Mercancías'}
            </h2>
            <EmptyReview onBackToHome={handleCompletion} />
          </div>
        </div>
      </div>
    );
  }

  // If review is completed
  if (modoRepaso) {
    return (
      <div className={styles.testPageContainer}>
        <div className={styles.testContent}>
          <div className={styles.container}>
            <ReviewCompletion
              remainingQuestions={preguntasFalladas.length}
              onRestartReview={handleRestartReview}
              onBackToHome={handleCompletion}
            />
          </div>
        </div>
      </div>
    );
  }

  // If there's no current question but there are questions in the list
  if (!preguntaActualObj) {
    if (preguntasFalladas.length > 0) {
      // This is a temporary state while adjusting the current question index
      return null;
    } else {
      return (
        <div className={styles.testPageContainer}>
          <div className={styles.testContent}>
            <div className={styles.container}>
              <FreeTrialLimitModal
                isOpen={showTrialLimitModal}
                onClose={() => setShowTrialLimitModal(false)}
              />
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'center' }}>
                Repaso de Preguntas Falladas {type === 'viajeros' ? 'Viajeros' : 'Mercancías'}
              </h2>
              <EmptyReview onBackToHome={handleCompletion} />
            </div>
          </div>
        </div>
      );
    }
  }

  // Main review content
  return (
    <>
      <div className={styles.testPageContainer}>
        <div className={styles.testContent}>
          <div className={styles.container}>
            <ReviewHeader
              title="Repaso de Preguntas Falladas"
              type={type}
              onClearFailures={handleLimpiarFallos}
            />
            
            <TestProgress current={preguntaActual} total={preguntasFalladas.length} />
            
            <TestQuestion
              pregunta={preguntaActualObj.pregunta}
              opciones={preguntaActualObj.opciones}
              letras={letras}
              respuestaActual={respuestaActual}
              respuestaCorrecta={preguntaActualObj.respuestaCorrecta}
              onRespuesta={handleRespuesta}
              mostrarFeedback={!!respuestaActual}
              disabled={!!respuestaActual}
              onPrevious={handlePreviousQuestion}
              onNext={handleNextQuestion}
              currentQuestion={preguntaActual}
              totalQuestions={preguntasFalladas.length}
              hasNextQuestion={hasNextQuestion}
              isPracticeMode={true}
            />

            {respuestaActual && (
              <ExplanationBox explanation={preguntaActualObj.explicacion} />
            )}
            
            <TestControls
              onSaltar={handleSaltar}
              onFinalizar={handleCompletion}
              isLastQuestion={isLastQuestion}
            />
          </div>
        </div>
      </div>
      
      {/* Confirmation modal for back navigation */}
      <ConfirmAbandonModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmAbandon}
      />
      
      {/* Free trial limit modal */}
      <FreeTrialLimitModal
        isOpen={showTrialLimitModal}
        onClose={() => setShowTrialLimitModal(false)}
      />
    </>
  );
};

const WrappedRepaso = () => (
  <ProtectedRoute>
    <Repaso />
  </ProtectedRoute>
);

export default WrappedRepaso;